import { useSelection } from "@/contexts/SelectionContext";
import { GetLinkedItemInfoDocument, ModelEnum } from "@/generated/graphql";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { scrollToItem } from "@/lib/utils/scrollToItem";
import { IconButton, Modal } from "@ai-teach/ui-web";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { type LinkProps, useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { FaUnlink } from "react-icons/fa";
import { MdExpandLess, MdExpandMore, MdLink } from "react-icons/md";

interface LinkedCopy {
  id: string;
  name: string;
}

interface LinkedCopiesSectionProps {
  linkedCopies: LinkedCopy[];
  type: "Slide" | "Lesson" | "Project";
  originalItemId: string;
}

const modelMapper = {
  Slide: ModelEnum.Slide,
  Lesson: ModelEnum.Lesson,
  Project: ModelEnum.Project,
};

export function LinkedCopiesSection({
  linkedCopies,
  type,
  originalItemId,
}: LinkedCopiesSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const currentPath = useRouterState({ select: (s) => s.location.pathname });

  const [getLinkedItemInfoQuery] = useLazyQuery(GetLinkedItemInfoDocument);

  const { toggleWarningModal, toggleUnlinkModalOpen } = useSelection();

  const hasItems = linkedCopies.length > 0;

  const shownItems = !hasItems ? [] : isExpanded ? linkedCopies : [linkedCopies[0]];

  const handleRedirect = async (itemId: string) => {
    const linkedItem = await getLinkedItemInfoQuery({
      variables: {
        id: itemId,
        model: modelMapper[type],
      },
    });

    const itemData = linkedItem.data?.linkedItemInfo;

    let url: LinkProps["to"] | null = null;

    if (!itemData) return;

    if (type === "Slide") {
      if (!itemData?.parentId) return;

      url = itemData?.isTrash ? "/admin/lesson/$lessonId/trash" : "/admin/lesson/$lessonId";
      const targetPath = url.replace("$lessonId", itemData.parentId);

      if (currentPath === targetPath) {
        scrollToItem("slide", itemId);
      } else {
        saveScrollTarget(`slide-${itemId}`, itemData.parentId);
        navigate({ to: url, params: { lessonId: itemData?.parentId } });
      }
      return;
    }

    const elementPrefix = type === "Lesson" ? "lesson" : "project";
    const pageKey = itemData.parentId || "home";

    if (itemData?.parentId) {
      url = itemData.isTrash ? "/admin/project/$projectId/trash" : "/admin/project/$projectId";
      const targetPath = url.replace("$projectId", itemData.parentId);

      if (currentPath === targetPath) {
        scrollToItem(elementPrefix as "lesson" | "project", itemId);
      } else {
        saveScrollTarget(`${elementPrefix}-${itemId}`, pageKey);
        navigate({ to: url, params: { projectId: itemData?.parentId } });
      }
      return;
    }

    url = itemData.isTrash ? "/admin/trash" : "/admin";
    if (currentPath === url) {
      scrollToItem(elementPrefix as "lesson" | "project", itemId);
    } else {
      saveScrollTarget(`${elementPrefix}-${itemId}`, pageKey);
      navigate({ to: url });
    }

    return;
  };

  return (
    <div className="relative mt-3 rounded-lg border border-secondary-100 dark:border-secondary-900/40 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary-50 dark:bg-secondary-900/20 border-b border-secondary-100 dark:border-secondary-900/40">
        <MdLink className="w-3.5 h-3.5 text-secondary-500 dark:text-secondary-400 shrink-0" />
        <span className="text-xs font-semibold text-secondary-600 dark:text-secondary-400 uppercase tracking-wide">
          Linked Copies
        </span>
        {hasItems && (
          <span className="ml-auto text-xs font-medium text-secondary-500 dark:text-secondary-400">
            {linkedCopies.length}
          </span>
        )}
      </div>
      <div className="px-3 py-2">
        <div className="label leading-relaxed transition-all duration-200">
          {shownItems.length !== 0 ? (
            shownItems
              .filter((item) => item !== undefined)
              .map((item) => (
                <div
                  key={`linked-copy-${item.id}`}
                  className="flex justify-between items-center py-0.5"
                >
                  <div
                    onClick={() => {
                      toggleWarningModal(
                        "You will be redirected to the linked item page. Continue?",
                        {
                          onCancel: () => {
                            toggleWarningModal(false);
                          },
                          onConfirm: () => handleRedirect(item.id),
                        }
                      );
                    }}
                    className="truncate text-secondary-600 dark:text-secondary-400"
                  >
                    {item.name}
                    {!isExpanded && linkedCopies.length > 1 && "..."}
                  </div>
                  <button
                    type="button"
                    className="text-secondary-400 hover:text-error-500 transition-colors ml-2 flex-shrink-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleUnlinkModalOpen({ type, itemId: item.id, originalItemId });
                    }}
                  >
                    <FaUnlink className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
          ) : (
            <p className="text-gray-400 dark:text-gray-500 text-sm italic">None</p>
          )}
        </div>
      </div>
      {linkedCopies.length > 1 && (
        <IconButton
          variant="primary"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
          }}
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full shadow-md"
          aria-label={isExpanded ? "Show less" : "Show more"}
        >
          {isExpanded ? <MdExpandLess className="w-4 h-4" /> : <MdExpandMore className="w-4 h-4" />}
        </IconButton>
      )}
    </div>
  );
}
